import axios from 'axios';
import { flatten } from 'lodash';
import getActionCable, { createWebsocketListener } from '../../../utils/actioncable';
import * as actions from './actions';
import { dashboardAuthHeader } from '../dashboards/selectors';
import { operations as groupOperations } from '../groups';
import { selectors as groupSelectors } from '../groups';
import { roundTimeDown } from '../../../utils/time';
import batchRequests from '../../../utils/batch-requests';
import moment from 'moment';

const BATCH_SIZE = 20;
const DEFAULT_LOOK_AHEAD = 75 * 60; // 1 hour and 15 minutes

// Return multiple availability requirement schedules that belong to the groups
// referenced by groupIds.
export const fetchMultipleByGroupId =
  ({ groupIds, look_ahead, start_time }) =>
  (dispatch, getState) => {
    const startTime = roundTimeDown(moment(start_time) || moment());

    const headers = dashboardAuthHeader(getState());
    // Look up the ids of availability requirements in hierarchy for the groups in
    // question
    return dispatch(groupOperations.fetch({ ids: groupIds, headers })).then(() => {
      let groups;
      groups = groupIds.some(Boolean)
        ? groupIds.map(g => groupSelectors.byId(getState())[g]).filter(Boolean)
        : groupSelectors.stations(getState());

      const ids = flatten(groups.map(group => (group.availability_requirements || []).map(ar => ar.id)));

      const endTime = startTime.clone().add(look_ahead || DEFAULT_LOOK_AHEAD, 'seconds');
      return dispatch(fetchMultiple({ ids, startTime, endTime }));
    });
  };

// Fetching schedules for multiple availability requirements in 1 go can be too
// taxing on the server. Split them up in request of BATCH_SIZE each using the
// batchRequests helper function.
export const fetchMultiple = batchRequests({
  batchSize: BATCH_SIZE,
  startLoadingCallback: actions.fetchAvailabilityRequirementScheduleStart(),
  finishLoadingCallback: actions.fetchAvailabilityRequirementScheduleSuccess(),
  executor:
    ({ ids, startTime, endTime }) =>
    (dispatch, getState) => {
      const params = {
        ids: ids.join(','),
        start_time: startTime.format(),
        end_time: endTime.format()
      };

      const url = '/api/v2/availability_requirements/schedules';
      const headers = dashboardAuthHeader(getState());

      return axios.get(url, { params, headers }).then(response => {
        const schedules = response.data;
        dispatch(actions.availabilityRequirementScheduleBatchUpdate(schedules, true));
      });
    }
});

export const registerForAvailabilityRequirementScheduleUpdates =
  ({ groupId, look_ahead, start_time }, actionCable) =>
  dispatch => {
    const initialLoad = () => {
      const groupIds = [groupId];
      // do an API call to get information about all the combined schedules that
      // are registered to. This is the baseline information load.
      dispatch(fetchMultipleByGroupId({ groupIds, look_ahead, start_time }));
    };

    const subscribeToWebsocket = () => {
      const channelData = {
        channel: 'AvailabilityRequirementNotificationsChannel',
        group_id: String(groupId),
        look_ahead: look_ahead || 0,
        start_time
      };

      const connectHandler = () => console.log('Connected to AvailabilityRequirementNotificationsChannel');

      const receivedHandler = data => {
        const { id } = data.availability_requirement;
        dispatch(actions.availabilityRequirementScheduleUpdated(id, data));
      };

      const listener = createWebsocketListener({
        dispatch,
        connectHandler,
        receivedHandler,
        fallbackPoller: null, // Poller disabled; restore with initialLoad
        fallbackPollerInterval: 30 * 60 * 1000
      });

      actionCable = actionCable || getActionCable();
      return actionCable.subscriptions.create(channelData, listener);
    };

    // Get the initial information via the API.
    initialLoad();
    // Subscribe to websocket for incremental updates
    return subscribeToWebsocket();
  };
